import { T } from '@transifex/react';
import { cn, Text, VStack } from '@vidahealth/vida-ui';
import { useWindowDimensions, View } from 'react-native';

import { CoachImage } from '@/components/CoachImage';
import { coachDisplayName } from '@/components/CoachSummary';
import type { MatchedCoach } from '@/modules/api';

import { NATIVE_CARD_SHADOW } from './sessionCardShadow';

const MD_BREAKPOINT = 520;

/**
 * Private to CoachMatchingSuccessScreen — the booked-session card: coach image
 * + "First session with {name}" + day/time + "Video call in Vida app". Flips to
 * a two-column layout at md+.
 */
export function SessionCard({
  coach,
  dayLabel,
  timeLabel,
}: {
  coach: Pick<MatchedCoach, 'displayName' | 'firstName' | 'title' | 'image'>;
  dayLabel: string;
  timeLabel: string;
}) {
  const { width } = useWindowDimensions();
  const isMd = width >= MD_BREAKPOINT;

  return (
    <View style={NATIVE_CARD_SHADOW} className='relative rounded-vui-lg shadow-hard-5'>
      <View className='overflow-hidden rounded-vui-lg border border-outline-50'>
        <View
          className={cn(
            'flex bg-background-0 dark:bg-background-50',
            isMd ? 'flex-row' : 'flex-col'
          )}
        >
          <CoachImage
            coach={coach}
            sizing={isMd ? 'fill' : 'fit'}
            containerStyle={
              isMd ? { width: '50%', alignSelf: 'stretch', height: 300 } : { width: '100%' }
            }
          />

          <View style={isMd ? { flex: 1 } : undefined}>
            <VStack space='lg' className='p-6'>
              <Text size='lg' weight='semibold' className='text-typography-900'>
                <T _str='First session with {name}' name={coachDisplayName(coach)} />
              </Text>
              <VStack space='xs'>
                <Text size='md' className='font-body text-typography-900'>
                  {dayLabel}
                </Text>
                <Text size='md' className='font-body text-typography-900'>
                  {timeLabel}
                </Text>
                <Text size='md' className='font-body text-typography-900'>
                  <T _str='Video call in Vida app' />
                </Text>
              </VStack>
            </VStack>
          </View>
        </View>
      </View>
    </View>
  );
}
